// WRAPPER AROUND LIBPHONENUMBER-JS PARSE FUNCTION
import {
  parsePhoneNumberFromString,
  isSupportedCountry,
  PhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import { DEFAULT_COUNTRY } from './constants';

export interface ParsedPhone {
  phoneNumber: PhoneNumber;
  e164: string;
  country: string | undefined;
  isValid: boolean;
}

// NARROW AN ARBITRARY COUNTRY STRING TO ONE LIBPHONENUMBER CAN PARSE.
// COUNTRY PICKERS AND LIBPHONENUMBER MAINTAIN SEPARATE LISTS THAT DO NOT AGREE
// (TERRITORIES LIKE AQ/BV/GS/HM/PN/TF/UM HAVE NO DIALING PLAN), SO AN UNCHECKED
// CAST WOULD PASS AN UNPARSEABLE COUNTRY INTO THE PARSER.
export const toCountryCode = (
  country: string | null | undefined,
): CountryCode | undefined => {
  if (!country) return undefined;
  return isSupportedCountry(country) ? country : undefined;
};

// PARSE A PHONE NUMBER STRING AND RETURN STRUCTURED DATA
//
// ⚠️ `country` APPLIES ONLY TO INPUT WITHOUT A LEADING "+" — IT IS THE
// ASSUMPTION WE MAKE ABOUT A NUMBER TYPED THE WAY THE PATIENT DIALS IT LOCALLY.
// OMITTING IT ASSUMES US, WHICH IS THE CU-86ajrx60c DEFECT: A NON-US NUMBER
// TYPED WITHOUT THE "+" PARSES AS AN INVALID US NUMBER AND IS REPORTED AS
// MALFORMED, SO THE PATIENT CONCLUDES THEIR NUMBER IS WRONG RATHER THAN THAT
// THEIR REGION IS UNSUPPORTED.
//
// THE DEFAULT IS PRESERVED SO EVERY EXISTING CALLER IS BEHAVIOUR-UNCHANGED.
// CALLERS THAT KNOW THE PATIENT'S SELECTED COUNTRY SHOULD PASS IT — THE FIVE
// ROUTE FORMS ALREADY TRACK ONE VIA `react-phone-number-input` AND ARE WIRED UP
// SEPARATELY (SEE THE TASK'S OUT-OF-SCOPE NOTE).
export const parsePhone = (
  value: string | null | undefined,
  country: string | null | undefined = DEFAULT_COUNTRY,
): ParsedPhone | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = `${value}`.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = parsePhoneNumberFromString(trimmed, {
      defaultCountry: toCountryCode(country),
      extract: true,
    });

    if (parsed) {
      return {
        phoneNumber: parsed,
        e164: parsed.format('E.164'),
        country: parsed.country,
        isValid: parsed.isValid(),
      };
    }
  } catch {
    // PARSE FAILED - RETURN NULL
  }

  return null;
};
